// Shared styles for the version-control window (ENG-539). Modeled on the
// PlayCanvas editor's VC picker: a large centered modal window with a two-pane
// (branches | checkpoints) body, side-panel confirmation flows, and a
// near-fullscreen graph. Kept in one module so a palette/layout tweak is one
// edit. Dark theme matched to the content browser.

export const C = {
  bg: '#0c0e14',
  bgDeep: '#080a10',
  bgRow: '#0a0c12',
  bgHover: '#121726',
  panel: '#10131c',
  border: '#1d2230',
  borderSoft: '#12161f',
  borderInput: '#2a3142',
  text: '#cdd3e0',
  textDim: '#697089',
  textMid: '#aeb6cc',
  accent: '#2b6cff',
  accentSoft: '#10233a',
  added: '#5fd35f',
  removed: '#e06c6c',
  modified: '#d8c45f',
  danger: '#e06c6c',
}

export const S = {
  // ── Modal shell ──────────────────────────────────────────────────────────
  // The overlay / window / windowMax trio that framed Version Control is gone:
  // it is a FloatingWindow now (70f43c0c), so its frame, geometry, drag, resize
  // and pop-out come from the one window implementation instead of being
  // re-spelled here. They were left behind as dead styles still sized in vw/vh.

  header: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: `1px solid ${C.border}`, background: C.bgDeep },
  title: { fontWeight: 600, letterSpacing: '.02em' },
  iconBtn: { background: 'transparent', color: C.textMid, border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '2px 4px' },

  // ── Two-pane body ─────────────────────────────────────────────────────────
  body: { flex: 1, display: 'flex', minHeight: 0 },
  leftPane: { width: 248, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', background: C.bgDeep },
  rightPane: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' },

  headerMain: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: `1px solid ${C.border}` },
  paneHeader: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: `1px solid ${C.border}` },
  paneTitle: { fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: C.textDim },
  scroll: { flex: 1, overflowY: 'auto' },

  // ── Branch list ─────────────────────────────────────────────────────────
  branchItem: { display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', cursor: 'pointer', borderBottom: `1px solid ${C.borderSoft}` },
  branchItemActive: { background: C.accentSoft },
  branchName: { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  branchDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },

  // ── Checkpoint list ─────────────────────────────────────────────────────
  cpTopBar: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderBottom: `1px solid ${C.border}` },
  dateHeader: { fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textDim, padding: '8px 12px 4px', background: C.bgDeep, position: 'sticky', top: 0 },
  row: { display: 'flex', gap: 9, padding: '8px 12px', alignItems: 'flex-start', borderBottom: `1px solid ${C.borderSoft}`, cursor: 'pointer' },
  rowSelected: { background: C.bgHover },
  rowPicked: { background: C.accentSoft },
  avatar: { width: 26, height: 26, borderRadius: '50%', color: '#fff', fontSize: 10, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cpLabel: { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  meta: { fontSize: 10.5, color: C.textDim, marginTop: 3 },

  // ── Buttons ───────────────────────────────────────────────────────────────
  primary: { background: C.accent, color: '#fff', border: 'none', borderRadius: 4, padding: '5px 11px', cursor: 'pointer', fontSize: 11 },
  ghost: { background: 'transparent', color: C.textMid, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: '4px 9px', cursor: 'pointer', fontSize: 11 },
  danger: { background: 'transparent', color: C.danger, border: '1px solid #4a2730', borderRadius: 4, padding: '4px 9px', cursor: 'pointer', fontSize: 11 },
  input: { background: C.bgRow, color: C.text, border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: '6px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' },

  // ── Generic ─────────────────────────────────────────────────────────────
  dim: { color: C.textDim },
  error: { color: C.danger, padding: '6px 12px' },
  empty: { color: C.textDim, padding: '18px 14px' },
  deletedChip: { display: 'inline-block', background: '#2a1a1a', border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 3, padding: '3px 7px', fontSize: 10, fontWeight: 500 },

  // ── Side panel (confirmation flows, slides over the right pane) ───────────
  sidePanel: { position: 'absolute', inset: 0, background: C.bg, display: 'flex', flexDirection: 'column', zIndex: 5 },
  sideHeader: { padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.bgDeep },
  sideTitle: { fontWeight: 600, fontSize: 13 },
  sideNote: { color: C.textDim, marginTop: 4, fontSize: 11.5, lineHeight: 1.4 },
  sideBody: { flex: 1, overflowY: 'auto', padding: 14 },
  sideButtons: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 14px', borderTop: `1px solid ${C.border}` },
  box: { background: C.bgRow, border: `1px solid ${C.border}`, borderRadius: 5, padding: 10, marginBottom: 10 },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 6 },

  // ── Context menu popup ────────────────────────────────────────────────────
  menu: { position: 'fixed', zIndex: 2100, background: C.panel, border: `1px solid ${C.borderInput}`, borderRadius: 6, padding: '4px 0', minWidth: 180, boxShadow: '0 12px 40px rgba(0,0,0,.5)' },
  menuItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' },
  menuItemDanger: { color: C.danger },
  menuSep: { height: 1, background: C.border, margin: '4px 0' },

  // ── Diff / changes ─────────────────────────────────────────────────────────
  diffBox: { padding: 9, background: C.bgRow, border: `1px solid ${C.border}`, borderRadius: 5 },
  diffRow: { fontSize: 11, padding: '1px 0' },
  pill: { background: '#161b27', border: `1px solid ${C.borderInput}`, borderRadius: 10, padding: '2px 8px', fontSize: 10.5 },

  // ── Graph modal (near-fullscreen over the overlay) ────────────────────────
  graphModal: { position: 'fixed', inset: '3%', zIndex: 2200, background: '#10161a', border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 24px 80px rgba(0,0,0,.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  graphHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: `1px solid ${C.border}`, background: C.bgDeep },
  graphCanvas: { flex: 1, overflow: 'auto', background: '#181f22' },
}

export function changeColor(type) {
  return type === 'added' ? C.added : type === 'removed' ? C.removed : C.modified
}

export function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

export function fmtTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}
